import api from '../axios'

/**
 * @description 购物车模块 API（TDC V3 规范）
 */

/**
 * @typedef {Object} ComboItemDto 套餐/组合的SKU配置项
 * @property {number|string} skuId - SKU ID
 * @property {number} [prodCount] - 购买数量（默认1）
 * @property {number} [comboItemType] - 套餐商品类型：1 主商品，2 搭配商品（仅套餐type=4使用）
 */

/**
 * @typedef {Object} ChangeShopCartItemV3
 * @property {number|string} [basketId] - 购物车 basketId（普通行更新当前行；套餐/组合配合完整comboItems原子替换整组）
 * @property {number|string} [prodId] - 商品 prodId（更新时可选，新增时普通商品必填，套餐/组合从comboItems取）
 * @property {number|string} [skuId] - SKU id（更新时可选，新增时普通商品必填，套餐/组合从comboItems取）
 * @property {number} [basketCount] - 购买数量（≥0；修改购物车时为绝对值，0 以服务端为准，删除请用 deleteItem）
 * @property {0|1} [isChecked] - 是否勾选：1 勾选，0 未勾选（默认1）
 * @property {0|1|2|3|4|5} [promotionType] - 活动类型：0 无活动，1 满量特价（自动匹配），2 满量折扣，3 买赠，4 搭配套餐，5 组合促销
 * @property {number|string} [promotionCode] - 活动编码（套餐type=4/组合type=5时必填）
 * @property {ComboItemDto[]} [comboItems] - 套餐/组合的SKU配置项（套餐type=4/组合type=5时必填，后端据此生成comboKey）
 */

export const cartApi = {
  /**
   * @description 获取购物车详情（V3）
   * @param {number|string} addrId - 地址 ID（0表示默认地址）
   * @returns {Promise<Object>} 购物车 data（含 shopCartItems、totalFinalAmount 等）
   */
  getCartInfo: (addrId = 0) => api.get(`/store/cart/info/v3`, { params: { addrId } }),
  getHeaderCartInfo: (addrId = 0) =>
    api.get('/store/cart/info/v3', {
      params: { addrId, scene: 'header' },
      showError: false,
    }),

  /**
   * @description 加入购物车或修改购物车（V3）
   * - 支持新增/修改/批量加购
   * - 勾选操作也通过此接口的 isChecked 参数实现
   * - 套餐(4)/组合促销(5)需传完整 comboItems，后端自动生成 comboKey；携带组内 basketId 时原子替换旧组
   * - 满量特价(1)不存储，购物车查询时自动匹配，加购时无需传活动参数
   * @param {ChangeShopCartItemV3 | ChangeShopCartItemV3[]} items - 单条或多条 V3 入参
   * @param {import('axios').AxiosRequestConfig} [config] - 请求配置（如 { showError: false }）
   * @returns {Promise<boolean>} 后端 data 字段（true 表示成功）
   */
  changeItems: (items, config) =>
    api.post('/store/cart/changeItem/v3', Array.isArray(items) ? items : [items], config),

  /**
   * @description 删除购物车商品（V3）
   * - 套餐/组合需整组删除（删除同 comboKey 的所有商品）
   * @param {Array<number|string>} basketIds - 购物车 basketId 数组
   * @returns {Promise<boolean>} 后端 data 字段（true 表示删除成功）
   */
  deleteItems: (basketIds) => api.delete('/store/cart/deleteItem/v3', { data: basketIds }),
}
